import React, { memo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { getDateTime, moment, magicNumbers } from 'utils';
import { UiIcon, UiTooltip } from 'ui-components';
import { useInterval } from 'hooks';
import {
  StyledNormalDateTime,
  StyledRelativeDateTime,
} from './RelativeDateTime.styles';

function NormalDateTime({
  className,
  date,
  time,
  dataTestid,
  hideIcon = false,
}) {
  return (
    <StyledNormalDateTime
      className={classNames('date-time flex normal-date-time', className)}
    >
      {time && (
        <span className='flex'>
          {!hideIcon && <UiIcon icon='ri-time-line' color='#9CAEBB' />}
          <span data-testid={`${dataTestid}NormalTime`}>{time}</span>
        </span>
      )}
      <span className='flex date-container'>
        {!hideIcon && <UiIcon icon='ri-calendar-event-line' color='#9CAEBB' />}
        <span data-testid={`${dataTestid}NormalDate`}>{date}</span>
      </span>
    </StyledNormalDateTime>
  );
}

NormalDateTime.propTypes = {
  className: PropTypes.string,
  dataTestid: PropTypes.string,
  date: PropTypes.string,
  time: PropTypes.string,
  hideIcon: PropTypes.bool,
};

function RelativeDateTime({
  className,
  dateTime,
  hideIcon = false,
  dataTestid = '',
  relativeUntil = {
    isActive: true,
    dateTime: magicNumbers.LAST_TWENTY_FOUR_HOURS,
    unit: 'hours',
  },
  isReactive = false,
  hideTime = false,
}) {
  const diff = moment(moment()).diff(moment(dateTime), relativeUntil.unit);
  const { date, time } = getDateTime(dateTime);

  const [, setRenderCount] = useState(0);

  useInterval(
    () => {
      setRenderCount((prev) => prev + 1);
    },
    isReactive ? magicNumbers.ONE_MINUTE_IN_MILLISECONDS : null
  );

  if (relativeUntil.isActive && diff > relativeUntil.dateTime) {
    return (
      <NormalDateTime
        date={date}
        time={hideTime ? null : time}
        dataTestid={dataTestid}
        hideIcon={hideIcon}
      />
    );
  }

  return (
    <StyledRelativeDateTime
      className={classNames('date-time flex relative-date-time', className)}
    >
      <UiTooltip
        className='tooltip'
        title={
          <NormalDateTime
            className='tooltip'
            date={date}
            time={time}
            dataTestid={`${dataTestid}normalDateTime`}
          />
        }
      >
        <span className='flex'>
          {!hideIcon && (
            <UiIcon icon='ri-time-line' color='#9CAEBB' hover='#9CAEBB' />
          )}
          <span
            className='from-now'
            data-testid={`${dataTestid}relativeDateTime`}
          >
            {moment(dateTime).fromNow()}
          </span>
        </span>
      </UiTooltip>
    </StyledRelativeDateTime>
  );
}

RelativeDateTime.propTypes = {
  dateTime: PropTypes.string,
  className: PropTypes.string,
  dataTestid: PropTypes.string,
  hideIcon: PropTypes.bool,
  relativeUntil: PropTypes.shape({
    isActive: PropTypes.bool,
    dateTime: PropTypes.number,
    unit: PropTypes.oneOf([
      'years',
      'months',
      'weeks',
      'days',
      'hours',
      'minutes',
    ]),
  }),
  isReactive: PropTypes.bool,
  hideTime: PropTypes.bool,
};

export default memo(RelativeDateTime);
